"use client";

import React, { useEffect, useRef, useState } from "react";
import { Linkedin, Twitter } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const teamMembers = [{
  name: "Anna Lindberg",
  position: "Grundare & Senior Rådgivare",
  bio: "Med över 15 års erfarenhet inom revision och ekonomistyrning hjälper Anna företag att navigera komplexa finansiella landskap med precision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1620476/img/jane_smith_headshot.png",
  linkedin: "#",
  twitter: "#"
}, {
  name: "Erik Johansson",
  position: "Redovisningskonsult",
  bio: "Erik är expert på digitalisering av ekonomiprocesser och brinner för att skapa effektiva, papperslösa arbetsflöden för våra klienter.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1620476/img/mike_johnson_headshot.png",
  linkedin: "#",
  twitter: "#"
}, {
  name: "Sofia Berg",
  position: "Lönespecialist & HR",
  bio: "Sofia ser till att personaladministration och lönehantering sker med exakthet, alltid med fokus på människan bakom siffrorna.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1620476/img/sara_lee_headshot.png",
  linkedin: "#",
  twitter: "#"
}];
export default function MeetTheTeam() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section data-section-id="69873" id="vårt-team" ref={ref} className="bg-[#F8F9FA] py-24 sm:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className={`flex flex-col items-center text-center mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl mb-4 text-[#11263C] font-sans">
            Vårt team
          </h2>
          <p className="text-slate-500 max-w-[800px] text-lg md:text-xl font-sans">
            Vi förenar logik med personligt engagemang för att skapa hållbar tillväxt och trygghet för din verksamhet.
          </p>
        </div>

        {}
        <div className="flex flex-wrap justify-center gap-8">
          {teamMembers.map((member, i) => <div key={i} data-index={i} className={`w-full sm:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)] min-w-[250px] transition-all duration-700 ease-out delay-${i * 100} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              <Card className="group h-full overflow-hidden border-none shadow-sm hover:shadow-xl transition-all duration-500 bg-white rounded-none">
                <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
                  <img src={member.image} alt={`Porträtt av ${member.name}`} className="w-full h-full object-cover object-center grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out transform group-hover:scale-105" />
                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#11263C]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                </div>
                <CardContent className="p-8 text-center flex flex-col items-center">
                  <h3 className="font-bold text-xl text-[#11263C] mb-1 tracking-tight font-sans">
                    {member.name}
                  </h3>
                  <p className="text-[#D64045] font-medium text-xs mb-4 uppercase tracking-widest font-sans">
                    {member.position}
                  </p>
                  <p className="text-slate-500 text-sm mb-6 line-clamp-3 leading-relaxed font-sans">
                    {member.bio}
                  </p>
                  <div className="flex items-center justify-center gap-4 mt-auto pt-4 border-t border-slate-100 w-full">
                    <Link to={member.linkedin} className="text-slate-400 hover:text-[#11263C] transition-colors duration-300" aria-label={`${member.name}s LinkedIn`}>
                      <Linkedin className="w-4 h-4" />
                    </Link>
                    <Link to={member.twitter} className="text-slate-400 hover:text-[#11263C] transition-colors duration-300" aria-label={`${member.name}s Twitter`}>
                      <Twitter className="w-4 h-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}